import { useEffect, useRef } from 'react'
import axiosAPI from '../services/axios'
import { TRACK_VISIT } from '../services/endpoints'

const DEDUP_WINDOW_MS = 1800000 // 30 minutes
const LAST_TRACKED_VISIT_KEY = 'lastTrackedVisit'

const readLastTrackedVisit = (): number | null => {
  try {
    const value = localStorage.getItem(LAST_TRACKED_VISIT_KEY)
    if (!value) return null
    const ts = new Date(value).getTime()
    return Number.isNaN(ts) ? null : ts
  } catch {
    return null
  }
}

const writeLastTrackedVisit = () => {
  try {
    localStorage.setItem(LAST_TRACKED_VISIT_KEY, new Date().toISOString())
  } catch {
    // localStorage unavailable — backend guard handles dedup
  }
}

const trackVisit = async () => {
  try {
    await axiosAPI.post(TRACK_VISIT())
    writeLastTrackedVisit()
  } catch (error) {
    console.error('Failed to track visit', error)
  }
}

export const useVisitTracker = (isAuthenticated: boolean): void => {
  const lastHiddenAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return

    // Initial mount: check localStorage for multi-tab dedup
    const lastTracked = readLastTrackedVisit()
    if (lastTracked === null || Date.now() - lastTracked >= DEDUP_WINDOW_MS) {
      trackVisit()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now()
      } else if (document.visibilityState === 'visible') {
        const lastHidden = lastHiddenAtRef.current
        if (lastHidden !== null && Date.now() - lastHidden >= DEDUP_WINDOW_MS) {
          trackVisit()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated])
}
